/**
 * RequisitesScreen — 3-step SBP requisites wizard at /requisites.
 *
 * Step 'bank': searchable bank picker (TOP_BANK_IDS when empty, all 206 on input).
 * Step 'phone': phone input → advance to fio.
 * Step 'fio': single "Фамилия Имя Отчество" input + submit → PUT /api/v2/buyer/profile/sbp.
 * Existing buyer with phone+bank but no ФИО (sbp_complete=false): auto-skip to 'fio'.
 * Re-entry (sbp_complete=true): read-view with ФИО + bank name + masked phone + "Изменить".
 *
 * Uses:
 *   - components/atoms: StaticPage, Card, PressBtn, Input, ListRow, EmptyState
 *   - hooks/usePlatformBack: TG back-button registration
 *   - api/client: apiFetch
 *   - lib/sbpBanks: SBP_BANKS, TOP_BANK_IDS
 *   - @tanstack/react-query: useQuery, useMutation, useQueryClient
 *   - react-router-dom: useNavigate
 *
 * Used by:
 *   - App.tsx (route /requisites, sibling of AppShell)
 */
import { useReducer, useMemo, useEffect, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { StaticPage, Card, PressBtn, Input, PhoneInput, ListRow, EmptyState, Ic, SectionHeader } from '../components/atoms';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { apiFetch } from '../api/client';
import { SBP_BANKS, TOP_BANK_IDS } from '../lib/sbpBanks';
import { P, S, T } from '../styles/tokens';

// ─── Types ────────────────────────────────────────────────────────────────────

// FTR-1102: fio_* split client-side from one input; server re-validates via
// validate_fio (SSOT). fio_patronymic optional (161-ФЗ "при наличии").
// sbp_phone/sbp_bank optional: the existing-buyer "add ФИО only" flow omits
// them and the backend carries over the saved values (raw phone is never
// exposed to the client — PII guard, profile.py EC-16).
type SbpPayload = {
  sbp_phone?: string;
  sbp_bank?: string;
  fio_last_name: string;
  fio_first_name: string;
  fio_patronymic?: string;
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

type Step = 'bank' | 'phone' | 'fio';

interface WizardState {
  step: Step;
  bank: string;
  bankSearch: string;
  phone: string;
  fio: string;
  errors: Record<string, string>;
  isSubmitting: boolean;
  submitted: boolean;
  /** True when user clicked "Изменить" in re-entry read-view — forces wizard mode */
  editing: boolean;
  /** True once the existing-buyer auto-skip-to-fio hydration has run (one-shot guard) */
  hydrated: boolean;
}

type WizardAction =
  | { type: 'SELECT_BANK'; bank: string }
  | { type: 'SET_BANK_SEARCH'; value: string }
  | { type: 'SET_PHONE'; value: string }
  | { type: 'ADVANCE_TO_FIO' }
  | { type: 'SET_FIO'; value: string }
  | { type: 'BACK'; to: Step }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_ERR'; errors: Record<string, string> }
  | { type: 'SUBMIT_OK' }
  | { type: 'ENTER_EDIT' }
  | { type: 'HYDRATE_FIO_STEP' };

const initialState: WizardState = {
  step: 'bank',
  bank: '',
  bankSearch: '',
  phone: '',
  fio: '',
  errors: {},
  isSubmitting: false,
  submitted: false,
  editing: false,
  hydrated: false,
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SELECT_BANK':
      // HARD-GATE: bank must be non-empty before advancing to phone step
      if (!action.bank) return state;
      return { ...state, bank: action.bank, bankSearch: '', step: 'phone', errors: {} };
    case 'SET_BANK_SEARCH':
      return { ...state, bankSearch: action.value };
    case 'SET_PHONE':
      return { ...state, phone: action.value, errors: { ...state.errors, phone: '' } };
    case 'ADVANCE_TO_FIO':
      return { ...state, step: 'fio', errors: {} };
    case 'SET_FIO':
      return { ...state, fio: action.value, errors: { ...state.errors, fio: '' } };
    case 'BACK':
      return { ...state, step: action.to, errors: {} };
    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, errors: {} };
    case 'SUBMIT_ERR':
      return { ...state, isSubmitting: false, errors: action.errors };
    case 'SUBMIT_OK':
      return { ...state, isSubmitting: false, submitted: true };
    case 'ENTER_EDIT':
      return { ...initialState, editing: true, hydrated: true };
    case 'HYDRATE_FIO_STEP':
      // Existing buyer (phone+bank present, ФИО missing) → start straight on fio.
      // bank/phone stay empty: the fio-only PUT omits them and the backend
      // carries over the saved values (raw phone is never sent to the client).
      return { ...state, step: 'fio', hydrated: true };
    default:
      return state;
  }
}

// ─── Phone validation (light client-side shape check) ─────────────────────────

const PHONE_RE = /^\+7\d{10}$/;

function validatePhone(phone: string): string | null {
  if (!phone.trim() || phone.trim() === '+7') return 'Введите номер телефона';
  if (!PHONE_RE.test(phone.trim())) return 'Введите номер полностью: +7 (903) 754-87-99';
  return null;
}

// ─── ФИО split + light client check ───────────────────────────────────────────
// One input → split on whitespace into last/first/patronymic. The server
// (validate_fio SSOT) does the real validation; this is only a shape gate so the
// user gets instant feedback before a round-trip. NO Cyrillic/length regex here
// (would duplicate the SSOT — see spec "БЕЗ дублирования validate_fio regex").

function splitFio(raw: string): { last: string; first: string; patronymic?: string } | null {
  const parts = raw.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return { last: parts[0], first: parts[1], patronymic: parts[2] };
}

// ─── Profile type ─────────────────────────────────────────────────────────────

interface ProfileView {
  sbp_complete: boolean;
  sbp_phone_masked?: string | null;
  sbp_bank_name?: string | null;
  fio_display?: string | null;
}

// ─── Bank filter (startsWith before includes, case-insensitive) ───────────────

function filterBanks(search: string): Array<[string, string]> {
  if (!search.trim()) {
    return TOP_BANK_IDS.map((id) => [id, SBP_BANKS[id]] as [string, string]);
  }
  const q = search.toLowerCase();
  const all = Object.entries(SBP_BANKS);
  const starts = all.filter(([, name]) => name.toLowerCase().startsWith(q));
  const includes = all.filter(
    ([, name]) => !name.toLowerCase().startsWith(q) && name.toLowerCase().includes(q),
  );
  return [...starts, ...includes];
}

// ─── Styles ───────────────────────────────────────────────────────────────────

// InsetList-style container: r=20, white bg, internal separators (prototype:374-380)
const bankListStyle: CSSProperties = {
  maxHeight: 360,
  overflowY: 'auto',
  borderRadius: 20,
  background: S.secondaryGroupedBg,
  border: '1px solid rgba(60,50,35,0.10)',
  boxShadow: '0 1px 3px rgba(45,37,24,0.04)',
};

// "Банк: Т-Банк" detail line under the step heading (prototype subheadline style)
const selectedBankStyle: CSSProperties = {
  font: T.subhead,
  color: S.secondaryLabel,
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

// ─── RequisitesScreen ─────────────────────────────────────────────────────────

export default function RequisitesScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  usePlatformBack();

  const onBack = () => navigate(-1);

  // Load profile to detect re-entry (sbp_complete)
  const { data: profile, isError: profileError } = useQuery<ProfileView>({
    queryKey: ['buyer', 'profile'],
    queryFn: () => apiFetch('/api/v2/buyer/profile'),
  });

  // FTR-1102 smart initial-step: existing buyer has phone+bank but no ФИО
  // (sbp_complete=false) → skip the bank/phone steps and land on 'fio'.
  // Detected via masked phone + resolved bank name present, fio_display absent
  // (no raw values needed — backend carries phone+bank on the fio-only PUT).
  // One-shot via `hydrated` guard.
  useEffect(() => {
    if (
      profile &&
      !profile.sbp_complete &&
      profile.sbp_phone_masked &&
      profile.sbp_bank_name &&
      !profile.fio_display &&
      !state.hydrated &&
      !state.editing &&
      state.step === 'bank'
    ) {
      dispatch({ type: 'HYDRATE_FIO_STEP' });
    }
  }, [profile, state.hydrated, state.editing, state.step]);

  // Submit mutation
  const mutation = useMutation<unknown, Error, SbpPayload>({
    mutationFn: (payload) =>
      apiFetch('/api/v2/buyer/profile/sbp', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['buyer', 'profile'] });
      dispatch({ type: 'SUBMIT_OK' });
    },
    onError: (err: Error) => {
      const msg = err.message ?? '';
      // Server validation errors (422) surface on the ФИО step — that is the
      // last input the user touched (phone shape is checked client-side first).
      if (msg.startsWith('API 422')) {
        dispatch({
          type: 'SUBMIT_ERR',
          errors: { fio: 'Проверь ФИО и телефон: формат не подошёл' },
        });
      } else {
        dispatch({ type: 'SUBMIT_ERR', errors: { general: 'Произошла ошибка, попробуй ещё раз' } });
      }
    },
  });

  // Handle 404 (flag-off)
  if (profileError) {
    return (
      <StaticPage title="Реквизиты" onBack={onBack}>
        <EmptyState title="Раздел недоступен" subtitle="Попробуй позже" />
      </StaticPage>
    );
  }

  // Success state
  if (state.submitted) {
    return (
      <StaticPage title="Реквизиты" onBack={onBack}>
        <Card p={20} r={20}>
          <div style={{ font: T.title3, color: S.label, marginBottom: 6 }}>
            Реквизиты сохранены
          </div>
          <div style={{ font: T.subhead, color: S.secondaryLabel, marginBottom: 20 }}>
            Теперь можно выводить деньги на карту
          </div>
          <PressBtn bg={P.success} onClick={() => navigate(-1)}>Готово</PressBtn>
        </Card>
      </StaticPage>
    );
  }

  // Re-entry read-view: sbp_complete=true (FTR-1102). InsetList of ФИО+банк+телефон.
  if (profile?.sbp_complete && !state.editing && !state.submitted) {
    const rows = [
      profile.fio_display    && { label: 'Получатель', value: profile.fio_display },
      profile.sbp_bank_name  && { label: 'Банк',       value: profile.sbp_bank_name },
      profile.sbp_phone_masked && { label: 'Телефон',  value: profile.sbp_phone_masked },
    ].filter(Boolean) as Array<{ label: string; value: string }>;
    return (
      <StaticPage title="Реквизиты" onBack={onBack}>
        <SectionHeader>Реквизиты добавлены</SectionHeader>
        <div style={{ background: S.secondaryGroupedBg, borderRadius: 20, overflow: 'hidden',
          border: `1px solid ${P.line}`, boxShadow: '0 1px 3px rgba(45,37,24,0.04)' }}>
          {rows.map((row, i) => (
            <ReadRow key={row.label} label={row.label} value={row.value} isLast={i === rows.length - 1} />
          ))}
        </div>
        <PressBtn bg="rgba(45,37,24,0.08)" fg={S.label} onClick={() => dispatch({ type: 'ENTER_EDIT' })}>
          Изменить
        </PressBtn>
        <PrivacyBanner />
      </StaticPage>
    );
  }

  return (
    <StaticPage title="Реквизиты" onBack={onBack}>
      {state.step === 'bank' && <BankStep state={state} dispatch={dispatch} />}
      {state.step === 'phone' && <PhoneStep state={state} dispatch={dispatch} />}
      {state.step === 'fio' && <FioStep state={state} dispatch={dispatch} mutation={mutation} />}
    </StaticPage>
  );
}

// ─── ReadRow — InsetRow-style label + value line (prototype:382-437 InsetRow)
// Left: label in S.label/bodyText; right: value in secondaryLabel/subhead.

function ReadRow({ label, value, isLast = false }: { label: string; value: string; isLast?: boolean }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', minHeight: 44,
      padding: '8px 16px', gap: 12, position: 'relative',
    }}>
      <div style={{ flex: 1, font: T.bodyText, color: S.label }}>{label}</div>
      <div style={{ font: T.subhead, color: S.secondaryLabel, flexShrink: 0 }}>{value}</div>
      {!isLast && (
        <div style={{
          position: 'absolute', bottom: 0, left: 16, right: 0,
          height: 0.5, background: S.separator,
        }} />
      )}
    </div>
  );
}

// ─── PrivacyBanner (lock + privacy notice) ────────────────────────────────────
// Matches prototype screens.jsx:806-809 cream bg + lock icon + text.

function PrivacyBanner() {
  return (
    <div style={{
      padding: 14,
      background: P.cream,
      borderRadius: 16,
      font: T.footnote,
      color: S.label,
      lineHeight: 1.5,
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
    }}>
      <Ic name="lock" size={14} color={P.muted} />
      <div>Данные нужны для перевода через СБП. Не передаём третьим лицам.</div>
    </div>
  );
}

// ─── BankStep ─────────────────────────────────────────────────────────────────

interface StepProps {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

function BankStep({ state, dispatch }: StepProps) {
  const filtered = useMemo(
    () => filterBanks(state.bankSearch),
    [state.bankSearch],
  );

  return (
    <>
      <Card p={16} r={20}>
        <div style={{ font: T.title3, color: S.label, marginBottom: 12 }}>
          Выберите банк
        </div>
        <Input
          value={state.bankSearch}
          onChange={(v) => dispatch({ type: 'SET_BANK_SEARCH', value: v })}
          placeholder="Поиск банка..."
          autoFocus
        />
      </Card>

      <SectionHeader>{state.bankSearch.trim() ? 'Результаты' : 'Популярные банки'}</SectionHeader>
      <div style={bankListStyle}>
        {filtered.map(([id, name], idx) => (
          <ListRow
            key={id}
            title={name}
            onClick={() => dispatch({ type: 'SELECT_BANK', bank: id })}
            isLast={idx === filtered.length - 1}
          />
        ))}
        {filtered.length === 0 && (
          <EmptyState title="Банк не найден" subtitle="Уточни название" />
        )}
      </div>
    </>
  );
}

// ─── PhoneStep ────────────────────────────────────────────────────────────────

function PhoneStep({ state, dispatch }: StepProps) {
  const bankName = SBP_BANKS[state.bank] ?? state.bank;

  function handleNext() {
    const phoneErr = validatePhone(state.phone);
    if (phoneErr) {
      dispatch({ type: 'SUBMIT_ERR', errors: { phone: phoneErr } });
      return;
    }
    dispatch({ type: 'ADVANCE_TO_FIO' });
  }

  return (
    <>
      <Card p={20} r={20}>
        <div style={{ font: T.title3, color: S.label, marginBottom: 4 }}>
          Номер телефона СБП
        </div>
        <div style={selectedBankStyle}>
          {`Банк: ${bankName}`}
        </div>

        <PhoneInput
          value={state.phone}
          onChange={(v) => dispatch({ type: 'SET_PHONE', value: v })}
          error={state.errors.phone}
          autoFocus
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <PressBtn bg={P.terracotta} onClick={handleNext}>Далее</PressBtn>
          <PressBtn
            onClick={() => dispatch({ type: 'BACK', to: 'bank' })}
            bg="rgba(45,37,24,0.08)"
            fg={S.label}
          >
            Назад
          </PressBtn>
        </div>
      </Card>
      <PrivacyBanner />
    </>
  );
}

// ─── FioStep ──────────────────────────────────────────────────────────────────

interface FioStepProps extends StepProps {
  mutation: UseMutationResult<unknown, Error, SbpPayload>;
}

function FioStep({ state, dispatch, mutation }: FioStepProps) {
  // bank may be empty when an existing buyer was hydrated straight to 'fio'
  // (the backend carries the saved bank), so only show the bank line if known.
  const bankName = state.bank ? (SBP_BANKS[state.bank] ?? state.bank) : '';

  function handleSubmit() {
    const parsed = splitFio(state.fio);
    if (!parsed) {
      dispatch({
        type: 'SUBMIT_ERR',
        errors: { fio: 'Укажи фамилию и имя (отчество — если есть)' },
      });
      return;
    }
    dispatch({ type: 'SUBMIT_START' });
    // New-buyer wizard carries bank+phone in state → send all 5. Existing-buyer
    // fio-only hydration leaves them empty → omit so the backend carries over.
    const payload: SbpPayload = {
      fio_last_name: parsed.last,
      fio_first_name: parsed.first,
      fio_patronymic: parsed.patronymic,
    };
    if (state.phone.trim()) payload.sbp_phone = state.phone.trim();
    if (state.bank) payload.sbp_bank = state.bank;
    mutation.mutate(payload);
  }

  return (
    <>
      <Card p={20} r={20}>
        <div style={{ font: T.title3, color: S.label, marginBottom: 4 }}>
          ФИО получателя
        </div>
        {bankName && (
          <div style={selectedBankStyle}>
            {`Банк: ${bankName}`}
          </div>
        )}

        <Input
          value={state.fio}
          onChange={(v) => dispatch({ type: 'SET_FIO', value: v })}
          placeholder="Фамилия Имя Отчество"
          error={state.errors.fio}
          autoFocus
        />
        <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 8 }}>
          Отчество — если есть. Имя должно совпадать с владельцем счёта.
        </div>

        {state.errors.general && (
          <div style={{ font: T.footnote, color: P.error, marginTop: 8 }}>
            {state.errors.general}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <PressBtn bg={P.terracotta} onClick={handleSubmit} disabled={state.isSubmitting}>
            {state.isSubmitting ? 'Сохраняем...' : 'Сохранить'}
          </PressBtn>
          {state.bank && (
            <PressBtn
              onClick={() => dispatch({ type: 'BACK', to: 'phone' })}
              bg="rgba(45,37,24,0.08)"
              fg={S.label}
            >
              Назад
            </PressBtn>
          )}
        </div>
      </Card>
      <PrivacyBanner />
    </>
  );
}
